<template>
  <div class="dpo-payment">
    <div class="redirect-card">
      <div class="redirect-content">
        <div class="redirect-icon redirect-icon--dpo">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
            <line x1="1" y1="10" x2="23" y2="10"></line>
          </svg>
        </div>

        <h3 class="redirect-title">Secure Online Payment</h3>
        <p class="redirect-message">
          You will be redirected to our secure payment gateway to complete your payment.
        </p>

        <div class="payment-summary">
          <div class="summary-row">
            <span class="summary-label">Original Amount</span>
            <span class="summary-value summary-value--original">{{ formatMoney(reference.amount) }}</span>
          </div>
          <div class="summary-row summary-row--discount">
            <span class="summary-label">{{ discountPercentage }}% Discount</span>
            <span class="summary-value summary-value--discount">-{{ formatMoney(discountAmount) }}</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-row summary-row--total">
            <span class="summary-label">You Pay</span>
            <span class="summary-value summary-value--total">{{ formatMoney(discountedAmount) }}</span>
          </div>
        </div>

        <div v-if="!error" class="redirect-spinner">
          <div class="spinner"></div>
          <span class="spinner-text">{{ statusText }}</span>
        </div>

        <p v-if="error" class="error-message">
          {{ error }}
          <button type="button" class="retry-button" @click="initiatePayment">
            Try Again
          </button>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import paymentService from '@/services/paymentService'

export default {
  name: 'DpoPayment',
  props: {
    reference: {
      type: Object,
      required: true
    },
    referenceId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isRedirecting: false,
      error: null,
      statusText: 'Preparing secure payment...'
    }
  },
  computed: {
    discountRate() {
      return typeof window.DPO_DISCOUNT === 'number' ? window.DPO_DISCOUNT : 0
    },
    discountPercentage() {
      return Number((this.discountRate * 100).toFixed(2))
    },
    discountAmount() {
      if (!this.reference || !this.reference.amount) return 0
      return Math.round((this.reference.amount * this.discountRate) * 100) / 100
    },
    discountedAmount() {
      if (!this.reference || !this.reference.amount) return 0
      return Math.round((this.reference.amount - this.discountAmount) * 100) / 100
    }
  },
  mounted() {
    this.initiatePayment()
  },
  methods: {
    formatMoney(value) {
      if (value == null) return ''
      return 'K ' + Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    async initiatePayment() {
      this.isRedirecting = true
      this.error = null
      this.statusText = 'Creating secure payment session...'

      try {
        const data = await paymentService.createDpoPayment(this.referenceId)

        if (!data || !data.paymentUrl) {
          throw new Error('Failed to get payment URL')
        }

        this.statusText = 'Redirecting to payment gateway...'

        // Small delay for user to see the status
        setTimeout(() => {
          window.open(data.paymentUrl, '_self')
        }, 500)
      } catch (err) {
        this.isRedirecting = false
        this.error = err.message || 'Failed to create payment. Please try again.'
        window.errorHandler.tomcatError(err)
      }
    }
  }
}
</script>

<style scoped>
.dpo-payment {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.redirect-card {
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 0.75rem;
  padding: 2rem;
}

.redirect-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.redirect-icon {
  width: 64px;
  height: 64px;
  background: #f4f4f5;
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
}

.redirect-icon--dpo {
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: #ffffff;
}

.redirect-icon--dpo svg {
  width: 32px;
  height: 32px;
}

.redirect-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #18181b;
  margin: 0 0 0.5rem 0;
}

.redirect-message {
  font-size: 0.875rem;
  color: #71717a;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
  max-width: 300px;
}

.payment-summary {
  background: #f4f4f5;
  border-radius: 0.5rem;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  min-width: 240px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.375rem 0;
}

.summary-label {
  font-size: 0.8125rem;
  color: #71717a;
}

.summary-value {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #18181b;
}

.summary-value--original {
  color: #71717a;
}

.summary-value--discount {
  color: #059669;
  font-weight: 600;
}

.summary-divider {
  height: 1px;
  background: #d4d4d8;
  margin: 0.5rem 0;
}

.summary-row--total .summary-label {
  font-weight: 600;
  color: #18181b;
}

.summary-value--total {
  font-size: 1.25rem;
  font-weight: 700;
  color: #059669;
}

.redirect-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e4e4e7;
  border-top-color: #059669;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spinner-text {
  font-size: 0.875rem;
  color: #71717a;
}

.error-message {
  color: #dc2626;
  font-size: 0.875rem;
  margin: 1rem 0 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.retry-button {
  background: #18181b;
  color: #ffffff;
  border: none;
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background 0.2s;
}

.retry-button:hover {
  background: #27272a;
}
</style>
